import { language } from './i18n';
import type { NativeBridge, Platform } from './types';
import { createWebPlatform } from './web';

declare global {
  interface Window {
    lumenNative?: NativeBridge;
  }
}

/** The bridge a native shell installed, if the app runs inside the Mac or iOS app. */
export function nativeBridge(win: Window = window): NativeBridge | null {
  return win.lumenNative ?? null;
}

/**
 * Platform for the Mac and iOS apps (ADR 0008): the web adapter (the shells host the same web build
 * in a web view), with links, tabs and the offline shell handed to the native side.
 */
export function createNativePlatform(win: Window, bridge: NativeBridge): Platform {
  const web = createWebPlatform(win);
  const server = bridge.server.replace(/\/$/, '');
  // The shell's menus and dialogs follow the page's language (chosen for the account, else the system's).
  bridge.post({ type: 'language', language: language() });
  return {
    ...web,
    native: bridge,
    info: { ...web.info, os: bridge.os },
    navigation: {
      ...web.navigation,
      // Links people copy point at the server, where the web app answers them; the app's own origin is local.
      absoluteUrl: (path: string) => new URL(path, `${server}/`).toString(),
    },
    // The shell knows the system appearance; `prefers-color-scheme` in its web views can lag behind (Electron).
    colorScheme: bridge.appearance && bridge.onAppearance
      ? { get: () => bridge.appearance!(), subscribe: (l) => bridge.onAppearance!(l) }
      : web.colorScheme,
    openExternal: (url) => bridge.openExternal(url),
    openInNewTab: (path) => bridge.openTab(path),
    // The shells bundle the app, so it starts without a network already.
    installOfflineShell: () => {},
  };
}
